import React, { useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import { formatCallDateDisplay } from "../../utils/callLogDates";
import "../../../user-builder/styles/userBuilderTable.css";

const COLUMN_VISIBILITY_KEY = "girinestham.admin.callLogs.columnVisibility";

const STATUS_TONE_MAP = {
  resolved: "occl-status--resolved",
  pending: "occl-status--pending",
  cancelled: "occl-status--cancelled",
  completed: "occl-status--resolved",
  open: "occl-status--pending",
  "in progress": "occl-status--inprogress",
};

/** Maps UI column id → backend sort_by */
const SORT_KEY_BY_COL = {
  date: "call_date",
  time: "call_time",
  caller_name: "caller_name",
  phone: "contact_number",
  call_type: "call_type",
  purpose: "purpose",
  handled_by: "handled_by",
  created_by: "created_by",
  status: "status",
  follow_up: "follow_up_date",
  follow_up_assigned_to: "follow_up_assigned_to",
  duration: "call_duration",
};

/** Shared with AdminCallLogFilters for column picker + Column → Value list. */
export const ADMIN_CALL_LOG_COLUMN_DEFS = [
  { id: "date", label: "Date", defaultVisible: true, sortable: true },
  { id: "time", label: "Time", defaultVisible: true, sortable: true },
  { id: "caller_name", label: "Caller Name", defaultVisible: true, sortable: true },
  { id: "phone", label: "Phone Number", defaultVisible: true, sortable: true },
  { id: "call_type", label: "Call Type", defaultVisible: true, sortable: true },
  { id: "purpose", label: "Purpose", defaultVisible: true, sortable: true },
  { id: "action", label: "Action Taken", defaultVisible: true, sortable: false },
  { id: "status", label: "Status", defaultVisible: true, sortable: true },
  { id: "handled_by", label: "Handled By", defaultVisible: true, sortable: true },
  { id: "created_by", label: "Created By", defaultVisible: true, sortable: true },
  { id: "follow_up", label: "Follow-up Date", defaultVisible: true, sortable: true },
  {
    id: "follow_up_assigned_to",
    label: "Follow-up assigned to",
    defaultVisible: true,
    sortable: true,
  },
  { id: "duration", label: "Duration", defaultVisible: true, sortable: true },
  { id: "remarks", label: "Remarks", defaultVisible: false, sortable: false },
  { id: "actions", label: "Actions", defaultVisible: true, sortable: false },
];

const COLUMN_DEFS = ADMIN_CALL_LOG_COLUMN_DEFS;

function formatFollowUpAssigneeCell(log) {
  if (!log?.follow_up_date) return "—";
  const explicitAssignee =
    log.follow_up_assigned_to != null && String(log.follow_up_assigned_to).trim() !== "";
  const name = explicitAssignee ? log.follow_up_assignee_name : log.handled_by_name;
  const role = explicitAssignee ? log.follow_up_assignee_role : log.handled_by_role;
  if (name && role) return `${name} (${role})`;
  return name || "—";
}

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

const AdminCallLogsTable = ({
  logs,
  loading,
  masterData,
  paginationMeta,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  sortBy,
  sortDir,
  onSortChange,
  showToolbar = true,
  columnVisibilityOverride = null,
}) => {
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) {
      // Always keep actions column visible so Edit/Delete are never hidden.
      if (c.id === "actions") {
        initial[c.id] = true;
      } else {
        initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
      }
    }
    return initial;
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen]);

  const visibleColumns = useMemo(() => {
    const source = columnVisibilityOverride || columnVisibility;
    return COLUMN_DEFS.filter((c) => {
      if (c.id === "actions") return true;
      return source[c.id] !== false;
    });
  }, [columnVisibility, columnVisibilityOverride]);

  const headers = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.id,
        label: c.label,
        className: c.id === "actions" ? "ub-table__col-actions" : "",
        sortable: c.sortable,
        sortKey: SORT_KEY_BY_COL[c.id],
      })),
    [visibleColumns]
  );

  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = COLUMN_DEFS.filter((col) => next[col.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const statusClass = (statusName) => {
    if (!statusName) return "";
    const key = statusName.toLowerCase().trim();
    return STATUS_TONE_MAP[key] || "occl-status--pending";
  };

  const handleSortClick = (col) => {
    if (!col.sortable || !col.sortKey || !onSortChange) return;
    const nextDir =
      sortBy === col.sortKey && sortDir === "desc"
        ? "asc"
        : sortBy === col.sortKey && sortDir === "asc"
          ? "desc"
          : "desc";
    onSortChange(col.sortKey, nextDir);
  };

  if (loading) {
    return (
      <div className="occl-panel">
        <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
          Loading call logs...
        </div>
      </div>
    );
  }

  const renderCell = (log, columnId) => {
    switch (columnId) {
      case "date":
        return log.call_date ? formatCallDateDisplay(log.call_date) : "—";
      case "time":
        return log.call_time || "—";
      case "caller_name":
        return (
          <span style={{ fontWeight: 500 }}>{log.caller_name || "—"}</span>
        );
      case "phone":
        return log.contact_number || "—";
      case "call_type":
        return log.call_type === true || log.call_type === 1 || String(log.call_type) === "1"
          ? "Incoming"
          : "Outgoing";
      case "purpose":
        return log.purpose_name || "—";
      case "action":
        return log.action_taken || "—";
      case "status":
        return <span className={`occl-status-tag ${statusClass(log.status_name)}`}>{log.status_name || "—"}</span>;
      case "handled_by":
        return log.handled_by_role
          ? `${log.handled_by_name || log.handled_by || "—"} (${log.handled_by_role})`
          : (log.handled_by_name || log.handled_by || "—");
      case "created_by":
        return log.created_by_name || "—";
      case "follow_up":
        return log.follow_up_date ? formatCallDateDisplay(log.follow_up_date) : "—";
      case "follow_up_assigned_to":
        return formatFollowUpAssigneeCell(log);
      case "duration":
        return log.call_duration ? `${log.call_duration} min` : "—";
      case "remarks":
        return log.remarks || "—";
      case "actions":
        return (
          <div className="ub-table-actions">
            <GlobalTableActionButton action="view" onClick={() => onView(log.id)} />
            <GlobalTableActionButton action="edit" onClick={() => onEdit(log.id)} />
            <GlobalTableActionButton
              action="delete"
              onClick={() => onDelete(log.id, log.caller_name)}
            />
          </div>
        );
      default:
        return "—";
    }
  };

  return (
    <div>
      {showToolbar ? (
        <div
          style={{
            display: "flex",
            gap: "15px",
            marginBottom: "1rem",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <div className="vt-column-picker" ref={pickerRef}>
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setColumnPickerOpen((o) => !o)}
              aria-expanded={columnPickerOpen}
              aria-controls="admin-call-logs-column-panel"
            >
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {columnPickerOpen && (
              <div
                id="admin-call-logs-column-panel"
                className="vt-column-picker__panel"
                role="group"
                aria-label="Choose visible columns"
              >
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {COLUMN_DEFS.filter((c) => c.id !== "actions").map((c) => (
                    <li key={c.id}>
                      <label className="vt-column-picker__row">
                        <input
                          type="checkbox"
                          checked={!!columnVisibility[c.id]}
                          onChange={() => toggleColumn(c.id)}
                        />
                        <span>{c.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <GlobalDataTable
        headers={headers.map(({ sortable, sortKey, ...h }) => {
          const active = sortable && sortKey === sortBy;
          return {
            ...h,
            label:
              sortable && onSortChange ? (
                <button
                  type="button"
                  onClick={() => handleSortClick({ sortable, sortKey })}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    font: "inherit",
                    cursor: "pointer",
                    color: active ? "var(--accent, #0ea5e9)" : "#475569",
                    fontWeight: active ? 700 : 600,
                    textAlign: "left",
                  }}
                >
                  {h.label}
                  {active ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                </button>
              ) : (
                h.label
              ),
          };
        })}
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Admin call logs table"
      >
        {!logs || logs.length === 0 ? (
          <tr>
            <td
              colSpan={headers.length}
              style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}
            >
              No records available
            </td>
          </tr>
        ) : (
          logs.map((log) => (
            <tr key={log.id}>
              {visibleColumns.map((col) => (
                <td key={col.id} className={col.id === "actions" ? "ub-table__col-actions" : ""}>
                  {renderCell(log, col.id)}
                </td>
              ))}
            </tr>
          ))
        )}
      </GlobalDataTable>

      {paginationMeta && paginationMeta.total > 0 && (
        <div className="occl-pagination">
          <div className="occl-pagination-stats">
            Showing <strong>{logs?.length ?? 0}</strong> of <strong>{paginationMeta.total}</strong>{" "}
            records (Page <strong>{paginationMeta.page}</strong> of{" "}
            <strong>{Math.ceil(paginationMeta.total / paginationMeta.limit) || 1}</strong>
            ). Max <strong>20</strong> rows per page.
          </div>
          <div className="occl-pagination-controls">
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={paginationMeta.page <= 1}
              onClick={() => onPageChange(paginationMeta.page - 1)}
            >
              Previous
            </GlobalButton>
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={
                paginationMeta.page >= Math.ceil(paginationMeta.total / paginationMeta.limit)
              }
              onClick={() => onPageChange(paginationMeta.page + 1)}
            >
              Next
            </GlobalButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCallLogsTable;
